<template>
  <div class="page profile">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader
      title="Contul meu"
      subtitle="Datele cu care intrați în aplicație. După salvare vă autentificați din nou."
      icon="mdi-account-cog-outline"
    />

    <div class="page-body">
      <div class="profile-grid">
        <section class="panel">
          <div class="panel-toolbar">
            <h2 class="panel-title">Datele dvs.</h2>
          </div>
          <v-form class="profile-form" @submit.prevent="showConfirmCard('updateUser')">
            <v-text-field
              v-model="userName"
              label="Login*"
              prepend-inner-icon="mdi-login"
              outlined
              :error-messages="formErrors.userName"
            ></v-text-field>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="firstName" label="Numele*" outlined :error-messages="formErrors.firstName"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="lastName" label="Prenumele*" outlined :error-messages="formErrors.lastName"></v-text-field>
              </v-col>
            </v-row>
            <v-text-field
              v-model="email"
              label="E-mail"
              prepend-inner-icon="mdi-email-outline"
              hint="Îl poate schimba doar DTI"
              persistent-hint
              outlined
              readonly
              background-color="#f5f5f5"
            ></v-text-field>
            <div class="profile-actions">
              <v-btn outlined large color="primary" @click="changePassword()">
                <v-icon left>mdi-lock-reset</v-icon>Schimbă parola
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn class="btn-green" large type="submit">
                <v-icon left>mdi-content-save-outline</v-icon>Salvează
              </v-btn>
            </div>
          </v-form>
        </section>

        <aside class="panel">
          <div class="panel-toolbar">
            <h2 class="panel-title">Rol și departamente</h2>
          </div>
          <dl class="facts">
            <dt>Rol</dt>
            <dd>{{ roleText }}</dd>
          </dl>
          <ul class="profile-depts">
            <li v-for="d in departments" :key="d.name">
              <span>{{ d.name }}</span>
              <span class="profile-depts__tags">
                <span v-if="d.isPrimary && departments.length > 1" class="tag">principal</span>
                <span v-if="d.isSef" class="tag tag--sef">Șef</span>
              </span>
            </li>
          </ul>
          <p class="side-note">Rolul și departamentele le schimbă Direcția Tehnologii Informaționale (tel. 52-388).</p>
        </aside>
      </div>
    </div>

    <ChangePassword
      ref="changePassword"
      :visible="showChangePasswordDialog"
      @password-changed="handlePasswordChanged"
      @loadData="fetchData"
    />
    <ConfirmCard
      v-if="confirmCard"
      :title="confirmTitle"
      @cancel="cancelConfirmCard"
      @confirm="confirmAction"
    />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import ChangePassword from "@/components/elements/ChangePassword.vue";
import ConfirmCard from "@/components/elements/ConfirmCard.vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import { roleLabel } from "@/config/roles";

export default {
  components: {
    ErrorAlert,
    SuccessAlert,
    ChangePassword,
    ConfirmCard,
    PageHeader,
  },
  data() {
    return {
      user: null,
      dialog: true,
      userName: "",
      firstName: "",
      lastName: "",
      idnp: "",
      email: "",
      password: "",
      errorMessage: "",
      successMessage: "",
      editedUser: null,
      showChangePasswordDialog: false,
      confirmCard: false,
      confirmTitle: "", 
      pendingAction: null, 
      formErrors: {
        userName: "",
        firstName: "",
        lastName: "",
        idnp: "",
        email: "",
        password: "",
      },
    };
  },
  computed: {
    roleText() {
      return roleLabel(this.$store.getters.getUser?.role);
    },
    departments() {
      return this.$store.getters.myDepartments;
    },
  },
  methods: {
    async fetchData() {
      try {
        this.user = await api.getUserById(this.$store.getters.getUser.userId);
        this.editedUser = { ...this.user };

        this.userName = this.user.user_name;
        this.firstName = this.user.first_name;
        this.lastName = this.user.last_name;
        this.idnp = this.user.idnp;
        this.email = this.user.email;
      } catch (error) {
        console.error("Error fetching roles:", error);
        this.$emit("error", error);
      }
    },
    async updateUser() {
      this.validateForm();

      if (!this.isFormValid()) {
        return;
      }

      try {
        this.editedUser.id = this.user.id;

        const userDataToUpdate = {
          id: this.editedUser.id,
          user_name: this.userName,
          first_name: this.firstName,
          last_name: this.lastName,
          idnp: this.idnp,
          email: this.email,
        };

        await api.updateUser(userDataToUpdate);
        this.handleSuccess("Datele au fost salvate. Vă rugăm să vă autentificați din nou.");
        setTimeout(() => {
          // this.$router.push({ name: "requests" });
          this.$store.dispatch("logout");
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error("Error updating user:", error);
        this.handleError("Datele nu au fost salvate. Încercați din nou.");
      }
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    resetForm() {
      this.formErrors = {
        userName: "",
        firstName: "",
        lastName: "",
        idnp: "",
        email: "",
        password: "",
      };
    },
    validateForm() {
      this.formErrors.userName = this.userName
        ? ""
        : "Loginul este obligatoriu";
      this.formErrors.firstName = this.firstName
        ? ""
        : "Numele este obligatoriu";
      this.formErrors.lastName = this.lastName
        ? ""
        : "Prenumele este obligatoriu";
      this.formErrors.email = this.validateEmail(this.email)
        ? ""
        : "Adresa de e-mail nu este validă";
      this.formErrors.idnp = !this.idnp || this.validateIDNP(this.idnp) ? "" : "IDNP invalid";
    },

    validateEmail(email) {
      const isValidEmail = helper.validateEmail(email);
      return isValidEmail;
    },
    validateIDNP(idnp) {
      const isValidIdnp = helper.validateIDNP(idnp);
      return isValidIdnp;
    },
    isFormValid() {
      return !Object.values(this.formErrors).some((error) => error);
    },
    openChangePasswordDialog() {
      this.showChangePasswordDialog = true;
    },
    async handlePasswordChanged() {
      this.handleSuccess(
        "Editare cu succes. Datele de logare au fost trimise pe poșta dumneavoastră."
      );
      setTimeout(() => {
        this.$store.dispatch("logout");
        window.location.reload();
      }, 1000);
    },
    changePassword() {
      this.$refs.changePassword.show();
    },
    showConfirmCard(action) {
      this.confirmCard = true;
      this.pendingAction = action;
      this.confirmTitle = "După salvare va trebui să vă logați din nou. Doriți să continuați?";
    },
    confirmAction() {
      this.confirmCard = false;
      if (this.pendingAction) {
        this[this.pendingAction]();
      }
    },
    cancelConfirmCard() {
      this.confirmCard = false;
      this.pendingAction = null;
    },
  },
  mounted() {
    this.fetchData();
    // this.$refs.changePassword.$on("error", this.handleError);
  },
  watch: {
    // showEditPopup(newVal) {
    //   if (!newVal) {
    //     this.resetForm();
    //   }
    // },
    userName(newVal) {
      this.formErrors.userName = "";
    },
    firstName(newVal) {
      this.formErrors.firstName = "";
    },
    lastName(newVal) {
      this.formErrors.lastName = "";
    },
    selectedDepartment(newVal) {
      this.formErrors.selectedDepartment = "";
    },
    idnp(newVal) {
      this.formErrors.idnp = "";
    },
    email(newVal) {
      this.formErrors.email = "";
    },
  },
};
</script>

<style scoped>
.profile-grid {
  display: grid;
  grid-template-columns: minmax(0, 640px) minmax(0, 420px);
  gap: 20px;
  align-items: start;
}
.profile-form {
  padding: 22px 20px 20px;
}
.profile-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}
.facts {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px 16px;
  margin: 0;
  padding: 16px 20px 4px;
}
.facts dt {
  color: var(--ink-3);
}
.facts dd {
  margin: 0;
  font-weight: 700;
}
.profile-depts {
  list-style: none;
  margin: 0;
  padding: 4px 20px 8px;
}
.profile-depts li {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  font-weight: 600;
}
.profile-depts li + li {
  border-top: 1px solid var(--line-soft);
}
.profile-depts__tags {
  display: flex;
  gap: 6px;
}
.tag {
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--slate-bg);
  color: var(--slate);
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
}
.tag--sef {
  background: var(--pine-900);
  color: var(--leaf);
}
.side-note {
  margin: 0;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--line-soft);
  color: var(--ink-2);
  font-size: 0.93rem;
}
@media (max-width: 1100px) {
  .profile-grid {
    grid-template-columns: 1fr;
  }
}
</style>
